import React, { useEffect, useRef, useState } from "react";

/**
 * DateInput — Day / Month / Year picker (Indian order), replaces browser-locale
 * type="date" inputs across the project. Emits value as "YYYY-MM-DD" for the API.
 *
 * The component keeps the partially-picked parts (e.g. only the Day chosen)
 * in internal state, and only emits to the parent once the date is complete —
 * previously any partial pick emitted "" which reset the selects instantly,
 * making the picker unusable everywhere.
 */
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const daysInMonth = (yy, mm) => new Date(yy || 2000, mm || 1, 0).getDate();
const pad = (n) => String(n).padStart(2, "0");

const parseValue = (v) => {
  const [yy, mm, dd] = (v || "").split("-").map((x) => parseInt(x, 10));
  return { d: dd || "", m: mm || "", y: yy || "" };
};

export default function DateInput({ value = "", onChange, required = false, className = "", yearsBack = 80, yearsAhead = 5, "data-testid": testId }) {
  // Internal draft of the three parts — survives partial selection
  const [part, setPart] = useState(() => parseValue(value));
  const lastEmitted = useRef(value || "");

  // Re-sync only when the parent changes the value externally
  // (e.g. form reset after submit, or loading an existing record to edit)
  useEffect(() => {
    if ((value || "") !== lastEmitted.current) {
      setPart(parseValue(value));
      lastEmitted.current = value || "";
    }
  }, [value]);

  const update = (patch) => {
    const next = { ...part, ...patch };
    // Clamp the day if the newly chosen month/year has fewer days (e.g. 31 → Feb)
    if (next.d && next.m && next.y) {
      const max = daysInMonth(next.y, next.m);
      if (next.d > max) next.d = max;
    }
    setPart(next);
    if (next.d && next.m && next.y) {
      const iso = `${next.y}-${pad(next.m)}-${pad(next.d)}`;
      lastEmitted.current = iso;
      onChange(iso);
    } else if (!next.d && !next.m && !next.y) {
      lastEmitted.current = "";
      onChange("");
    }
    // Partial selection → keep it locally, emit nothing yet
  };

  const { d, m, y } = part;
  const nowY = new Date().getFullYear();
  const years = [];
  for (let yy = nowY + yearsAhead; yy >= nowY - yearsBack; yy--) years.push(yy);
  // Until a month/year is chosen, allow all 31 days so Day can be picked first
  const maxDay = m && y ? daysInMonth(y, m) : 31;

  const sel = "border border-line px-2 py-2 bg-pearl text-sm rounded-md";
  return (
    <div className={`flex gap-2 ${className}`} data-testid={testId}>
      <select required={required} className={`${sel} w-[30%]`} value={d || ""} onChange={(e) => update({ d: +e.target.value || "" })} aria-label="Day">
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map((dd) => <option key={dd} value={dd}>{dd}</option>)}
      </select>
      <select required={required} className={`${sel} flex-1`} value={m || ""} onChange={(e) => update({ m: +e.target.value || "" })} aria-label="Month">
        <option value="">Month</option>
        {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
      </select>
      <select required={required} className={`${sel} w-[30%]`} value={y || ""} onChange={(e) => update({ y: +e.target.value || "" })} aria-label="Year">
        <option value="">Year</option>
        {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}

/** Format an ISO date/datetime for display in Indian day/month/year order. */
export const fmtDateIN = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt)) return iso;
  return dt.toLocaleDateString("en-GB"); // dd/mm/yyyy
};
